if (!customElements.get('collection-card-colors')) {
  const cache = new Map();
  customElements.define('collection-card-colors', class extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.syncVisibleColors = this.syncVisibleColors.bind(this);
    }

    connectedCallback() {
      this.card = this.closest('card-product-siblings');
      this.mobile = window.matchMedia('(max-width: 899px)');
      this.mobile.addEventListener('change', this.syncVisibleColors);
      this.addEventListener('click', this.onClick);
      this.syncVisibleColors();
      // A color filter is active: show the card in the first matching color.
      const preset = this.querySelector('[data-filter-selected]');
      if (preset) this.select(preset);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.mobile?.removeEventListener('change', this.syncVisibleColors);
      this.request?.abort();
    }

    syncVisibleColors() {
      const limit = this.mobile.matches && this.card?.classList.contains('catalog-card') && this.closest('.collection-refinements') ? 3 : 5;
      const more = this.querySelector('[data-more-colors]');
      const expanded = more?.getAttribute('aria-expanded') === 'true';
      const colors = [...this.querySelectorAll('[data-card-color]')];
      // Show a window of `limit` swatches centered on the selected color.
      const selected = Math.max(0, colors.findIndex(button => button.getAttribute('aria-pressed') === 'true'));
      const start = Math.max(0, Math.min(selected - Math.floor(limit / 2), colors.length - limit));
      colors.forEach((button, index) => {
        button.removeAttribute('data-outside-mobile');
        button.hidden = !expanded && (index < start || index >= start + limit);
      });
      if (more) {
        more.removeAttribute('data-outside-desktop');
        more.hidden = colors.length <= limit;
        more.textContent = expanded ? '−' : `+${colors.length - limit}`;
      }
    }

    onClick(event) {
      if (event.target.closest('[data-more-colors]')) return;
      const button = event.target.closest('[data-card-color]');
      if (!button || button.getAttribute('aria-pressed') === 'true') return;
      this.select(button);
    }

    async select(button) {
      this.request?.abort();
      this.request = new AbortController();
      const request = this.request;
      let fragment;
      if (button.hasAttribute('data-sibling')) {
        this.setAttribute('aria-busy', 'true');
        const view = this.card.classList.contains('catalog-card') ? 'catalog-card-sibling' : 'card-pro-sibling';
        const cacheKey = `${view}:${button.dataset.url}`;
        try {
          if (!cache.has(cacheKey)) {
            const url = new URL(button.dataset.url, window.location.origin);
            url.searchParams.set('view', view);
            const response = await fetch(url, { signal: request.signal });
            if (!response.ok) throw new Error('Unable to load product');
            cache.set(cacheKey, await response.text());
          }
          if (request.signal.aborted) return;
          fragment = new DOMParser().parseFromString(cache.get(cacheKey), 'text/html');
        } catch {
          return;
        } finally {
          if (this.request === request) this.removeAttribute('aria-busy');
        }
      }
      this.querySelectorAll('[data-card-color]').forEach(color => color.setAttribute('aria-pressed', String(color === button)));
      const title = this.card.querySelector('.card__title a');
      const imageLink = this.card.querySelector('card-gallery a.card');
      [title, imageLink, this.querySelector('[data-more-colors]')].forEach(link => { if (link) link.href = button.dataset.url; });
      if (title) title.textContent = button.dataset.title;
      imageLink?.setAttribute('aria-label', button.dataset.title);
      const price = this.card.querySelector('.card__price');
      if (fragment) {
        const shortName = this.card.querySelector('.collection-card__short-name');
        shortName.textContent = button.dataset.shortName || button.dataset.title || '';
        shortName.hidden = !shortName.textContent;
        if (price) price.innerHTML = fragment.querySelector('[data-sib-catalog-price]')?.content.querySelector('.card__price')?.innerHTML || fragment.querySelector('[data-sib-price]').innerHTML;
        const rating = fragment.querySelector('[data-sib-rating]');
        const slot = this.card.querySelector('.card__rating-slot');
        if (slot) {
          slot.innerHTML = Number(rating?.dataset.sibRatingValue) > 0 && Number(rating?.dataset.sibRatingCount) > 0 ? rating.innerHTML : '';
          if (this.card.classList.contains('catalog-card') && slot.firstElementChild) {
            const stars = slot.querySelector('.wt-rating');
            stars.style.setProperty('--catalog-rating', rating.dataset.sibRatingValue);
            stars.querySelector('.wt-rating__count')?.remove();
            const counter = stars.querySelector('.wt-rating__counter');
            if (counter) counter.textContent = `(${rating.dataset.sibRatingCount})`;
          }
          const titleRow = slot.closest('.catalog-card__subtitle-review-row');
          if (titleRow) titleRow.hidden = !button.dataset.shortName && !slot.firstElementChild;
          if (title) title.closest('.card__title').hidden = !button.dataset.shortName;
        }
        this.updateGallery(fragment.querySelector('[data-sib-track]').innerHTML);
        if (this.card.classList.contains('catalog-card')) this.updateHoverImage(fragment.querySelector('[data-sib-hover-image]')?.dataset.sibHoverImage);
        const qbp = fragment.querySelector('[data-sib-qbp]');
        this.card.querySelector('quick-size-picker')?.refreshVariants?.({
          rowsHtml: fragment.querySelector('[data-sib-qbp-rows]')?.innerHTML || '',
          singleId: qbp?.dataset.singleId,
          singleAvailable: qbp?.dataset.singleAvailable === 'true',
        });
      } else {
        if (price) price.replaceChildren(button.querySelector('[data-color-price]').content.cloneNode(true));
        if (button.dataset.image) {
          const original = this.card.querySelector('card-gallery img');
          if (original) {
            const image = original.cloneNode();
            image.src = button.dataset.image;
            image.removeAttribute('srcset');
            image.alt = button.dataset.title;
            const slide = document.createElement('div');
            slide.className = 'card-pro__gallery-slide';
            slide.append(image);
            const track = this.card.querySelector('[data-gallery-track]');
            if (track) this.updateGallery(slide.outerHTML);
            else original.replaceWith(image);
          }
        }
      }
      if (!fragment) this.updateHoverImage(button.dataset.hoverImage);
      this.card.querySelectorAll('quick-add').forEach(quickAdd => {
        if (button.dataset.handle) quickAdd.setAttribute('data-product-handle', button.dataset.handle);
        const quickButton = quickAdd.querySelector('button');
        if (quickButton) quickButton.dataset.productUrl = button.dataset.url;
      });
    }

    updateHoverImage(src) {
      const image = this.card.querySelector('[data-card-hover-image]');
      if (!image) return;
      image.hidden = !src;
      if (src) image.src = src;
      else image.removeAttribute('src');
      image.alt = '';
    }

    updateGallery(html) {
      const track = this.card.querySelector('[data-gallery-track]');
      if (!track) return;
      const template = document.createElement('template');
      template.innerHTML = html;
      const limit = Number(this.card.dataset.galleryLimit) || 5;
      track.replaceChildren(...[...template.content.children].slice(0, limit));
      const indicator = this.card.querySelector('[data-gallery-indicator]');
      if (indicator) {
        indicator.replaceChildren();
        [...track.children].forEach((slide, index) => {
          const segment = document.createElement('span');
          segment.className = `card-pro__gallery-indicator__seg${index === 0 ? ' is-active' : ''}`;
          indicator.append(segment);
        });
      }
      this.card.querySelector('card-gallery')?.refresh?.();
      this.updateHoverImage(track.children[1]?.querySelector('img')?.src);
    }
  });
}
